import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { adminApi } from "../../api/adminApi";
import type {
  FieldMapping,
  ReportDefinition,
  ReportParamDefinition,
  ReportTemplateVersion,
  TemplateSnippet,
} from "../../api/types";
import { ReportTabs } from "../../components/ReportTabs";
import { JsonEditor } from "../../components/JsonEditor";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { STARTER_TEMPLATES, SNIPPETS, type StarterTemplate } from "./starterTemplates";
import { useTheme } from "../../theme/ThemeContext";

const EXPORT_FORMATS = ["pdf", "xlsx", "docx", "csv"] as const;

type ViewMode = "split" | "code" | "preview";
const VIEW_MODES: { id: ViewMode; label: string }[] = [
  { id: "code", label: "Editor" },
  { id: "split", label: "Split" },
  { id: "preview", label: "Preview" },
];
const ZOOM_LEVELS: { id: "fit" | number; label: string }[] = [
  { id: "fit", label: "Fit" },
  { id: 0.75, label: "75%" },
  { id: 1, label: "100%" },
  { id: 1.25, label: "125%" },
];
// A4 at 96dpi - the preview draws a real page so margins and page breaks read like the PDF.
const PAPER_LONG = 1123;
const PAPER_SHORT = 794;

const DEFAULT_HTML = `<!doctype html>
<html>
  <head>
    <style>
      body { font-family: sans-serif; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 4px 6px; }
    </style>
  </head>
  <body>
    <h2>{{report.displayName}}</h2>
    <p>Generated for {{params.dateFrom}} - {{params.dateTo}}</p>
  </body>
</html>
`;

const DEFAULT_SAMPLE_DATA = `{
  "report": { "displayName": "Sample Report" },
  "params": { "dateFrom": "2026-01-01", "dateTo": "2026-01-31" }
}`;

export default function ReportDesigner() {
  const { reportId = "" } = useParams();
  const { isDark, textSize } = useTheme();
  const monacoFontSize = textSize === "small" ? 12 : textSize === "large" ? 15 : 13;
  const [report, setReport] = useState<ReportDefinition | null>(null);
  const [params, setParams] = useState<ReportParamDefinition[]>([]);
  const [mapping, setMapping] = useState<FieldMapping[]>([]);
  const [versions, setVersions] = useState<ReportTemplateVersion[]>([]);
  const [activeVersion, setActiveVersion] = useState<ReportTemplateVersion | null>(null);

  const [html, setHtml] = useState(DEFAULT_HTML);
  const [sampleData, setSampleData] = useState(DEFAULT_SAMPLE_DATA);
  const [showSampleData, setShowSampleData] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<(typeof EXPORT_FORMATS)[number]>("pdf");
  const [downloading, setDownloading] = useState(false);
  const [pendingDeleteVersion, setPendingDeleteVersion] = useState<ReportTemplateVersion | null>(null);
  const [deletingVersion, setDeletingVersion] = useState(false);
  const [useLiveData, setUseLiveData] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem("reportforge.designer.viewMode");
      return saved === "code" || saved === "preview" || saved === "split" ? saved : "split";
    } catch {
      return "split";
    }
  });
  const [zoom, setZoom] = useState<"fit" | number>("fit");
  const [canvasWidth, setCanvasWidth] = useState(0);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [customSnippets, setCustomSnippets] = useState<TemplateSnippet[]>([]);
  const [savingSnippetName, setSavingSnippetName] = useState<string | null>(null);
  const [savingSnippet, setSavingSnippet] = useState(false);
  const [pendingDeleteSnippet, setPendingDeleteSnippet] = useState<TemplateSnippet | null>(null);
  const [deletingSnippet, setDeletingSnippet] = useState(false);

  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the last html/sampleData actually persisted (by autosave or a manual save), so autosave
  // only fires when the editor content has genuinely changed since - never on initial load, and
  // never twice for the same content.
  const lastSavedRef = useRef<{ html: string; sampleData: string } | null>(null);
  const activeVersionRef = useRef<ReportTemplateVersion | null>(null);

  useEffect(() => {
    Promise.all([
      adminApi.reports.get(reportId),
      adminApi.reports.getParams(reportId),
      adminApi.reports.getMapping(reportId),
      adminApi.reports.listTemplateVersions(reportId),
      adminApi.snippets.list(),
    ])
      .then(([r, p, m, v, snippets]) => {
        setReport(r);
        if (r.defaultFormat) setDownloadFormat(r.defaultFormat as (typeof EXPORT_FORMATS)[number]);
        setParams(p);
        setMapping(m);
        setVersions(v);
        setCustomSnippets(snippets);
        const draft = v.find((ver) => ver.status === "DRAFT") ?? v[v.length - 1] ?? null;
        let loadedHtml = DEFAULT_HTML;
        let loadedSampleData = DEFAULT_SAMPLE_DATA;
        if (draft) {
          setActiveVersion(draft);
          activeVersionRef.current = draft;
          loadedHtml = draft.htmlContent;
          loadedSampleData = draft.sampleDataJson || DEFAULT_SAMPLE_DATA;
          setHtml(loadedHtml);
          setSampleData(loadedSampleData);
        }
        lastSavedRef.current = { html: loadedHtml, sampleData: loadedSampleData };
        if (v.length === 0) setShowTemplatePicker(true);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load designer"))
      .finally(() => setLoading(false));
  }, [reportId]);

  function applyStarterTemplate(tpl: StarterTemplate) {
    setHtml(tpl.html);
    // Templates that ship sample data (so the preview isn't a page of empty cells) replace it;
    // the ones that don't leave whatever sample data is already there.
    if (tpl.sampleData !== undefined) setSampleData(JSON.stringify(tpl.sampleData, null, 2));
    setShowTemplatePicker(false);
  }

  // When live data is on, params defaults feed the endpoint call (e.g. a POST body param the
  // endpoint needs to return anything), same as a real run would send.
  const liveParams = useMemo(() => {
    const obj: Record<string, unknown> = {};
    params.forEach((p) => {
      if (p.defaultValue !== undefined && p.defaultValue !== null && p.defaultValue !== "") {
        obj[p.paramKey] = p.defaultValue;
      }
    });
    return obj;
  }, [params]);

  const runPreview = useCallback(
    (htmlValue: string, sampleValue: string) => {
      adminApi.reports
        .previewTemplate(reportId, {
          htmlContent: htmlValue,
          sampleDataJson: sampleValue,
          useLiveData,
          params: useLiveData ? liveParams : undefined,
        })
        .then((res) => {
          setPreviewHtml(res.renderedHtml);
          setPreviewError(null);
        })
        .catch((err) => setPreviewError(err instanceof Error ? err.message : "Preview failed"));
    },
    [reportId, useLiveData, liveParams]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runPreview(html, sampleData), 600);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [html, sampleData, runPreview]);

  const BUILT_IN_VARIABLES = [
    "report.displayName",
    "report.reportKey",
    "theme.primaryColor",
    "theme.secondaryColor",
    "theme.accentColor",
    "theme.logoUrl",
    "theme.companyName",
    "theme.email",
    "theme.phone",
    "theme.address",
  ];

  const variables = useMemo(() => {
    const paramVars = params.map((p) => `params.${p.paramKey}`).filter(Boolean);
    const mappedVars = mapping.map((m) => m.targetVariable).filter(Boolean);
    return Array.from(new Set([...paramVars, ...mappedVars]));
  }, [params, mapping]);

  function insertVariable(name: string) {
    const ed = editorRef.current;
    const selection = ed?.getSelection();
    if (!ed || !selection) return;
    const token = `{{${name}}}`;
    ed.executeEdits("insert-variable", [{ range: selection, text: token, forceMoveMarkers: true }]);
    ed.focus();
  }

  useEffect(() => {
    try {
      localStorage.setItem("reportforge.designer.viewMode", viewMode);
    } catch {
      /* storage unavailable - the choice just won't persist */
    }
  }, [viewMode]);

  // "Fit" needs the canvas width, and the canvas remounts whenever the preview pane does (view
  // mode switch, preview error clearing), so re-attach the observer on those.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const measure = () => setCanvasWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, viewMode, previewError]);

  function insertSnippet(text: string) {
    const ed = editorRef.current;
    const selection = ed?.getSelection();
    if (!ed || !selection) return;
    ed.executeEdits("insert-snippet", [{ range: selection, text, forceMoveMarkers: true }]);
    ed.focus();
  }

  // Turns whatever's currently highlighted in the editor into a reusable snippet, so a header or
  // block built once in this report can be dropped straight into any other report's template.
  function startSaveSelectionAsSnippet() {
    const ed = editorRef.current;
    const selection = ed?.getSelection();
    const text = selection && ed ? ed.getModel()?.getValueInRange(selection) : "";
    if (!text || !text.trim()) {
      setError("Select some HTML in the template first, then \"Save selection as snippet\".");
      return;
    }
    setSavingSnippetName("");
  }

  async function confirmSaveSnippet() {
    const ed = editorRef.current;
    const selection = ed?.getSelection();
    const text = selection && ed ? ed.getModel()?.getValueInRange(selection) : "";
    if (!savingSnippetName?.trim() || !text?.trim()) return;
    setSavingSnippet(true);
    setError(null);
    try {
      const saved = await adminApi.snippets.create({ name: savingSnippetName.trim(), htmlContent: text });
      setCustomSnippets((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
      setSavingSnippetName(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save snippet");
    } finally {
      setSavingSnippet(false);
    }
  }

  async function handleDeleteSnippet() {
    if (!pendingDeleteSnippet) return;
    setDeletingSnippet(true);
    try {
      await adminApi.snippets.remove(pendingDeleteSnippet.id);
      setCustomSnippets((prev) => prev.filter((s) => s.id !== pendingDeleteSnippet.id));
      setPendingDeleteSnippet(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete snippet");
      setPendingDeleteSnippet(null);
    } finally {
      setDeletingSnippet(false);
    }
  }

  const persistDraft = useCallback(
    async (htmlValue: string, sampleValue: string) => {
      const current = activeVersionRef.current;
      let saved: ReportTemplateVersion;
      if (current && current.status === "DRAFT") {
        saved = await adminApi.reports.updateDraftTemplate(reportId, current.id, {
          htmlContent: htmlValue,
          sampleDataJson: sampleValue,
        });
        setVersions((prev) => prev.map((v) => (v.id === saved.id ? saved : v)));
      } else {
        saved = await adminApi.reports.saveDraftTemplate(reportId, {
          htmlContent: htmlValue,
          sampleDataJson: sampleValue,
        });
        setVersions((prev) => [...prev, saved]);
      }
      activeVersionRef.current = saved;
      setActiveVersion(saved);
      lastSavedRef.current = { html: htmlValue, sampleData: sampleValue };
      return saved;
    },
    [reportId]
  );

  async function handleSaveDraft() {
    if (autosaveDebounceRef.current) clearTimeout(autosaveDebounceRef.current);
    setSaving(true);
    setError(null);
    try {
      await persistDraft(html, sampleData);
      setAutosaveStatus("saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save draft");
    } finally {
      setSaving(false);
    }
  }

  // Autosaves ~3s after the user stops typing, so work in a brand-new report (which has no draft
  // version at all until something is saved) survives an accidental tab close or navigation - the
  // manual "Save draft" button above remains for an explicit, immediate save.
  useEffect(() => {
    if (loading) return;
    const last = lastSavedRef.current;
    if (last && last.html === html && last.sampleData === sampleData) return;

    if (autosaveDebounceRef.current) clearTimeout(autosaveDebounceRef.current);
    autosaveDebounceRef.current = setTimeout(async () => {
      setAutosaveStatus("saving");
      try {
        await persistDraft(html, sampleData);
        setAutosaveStatus("saved");
      } catch {
        setAutosaveStatus("error");
      }
    }, 3000);

    return () => {
      if (autosaveDebounceRef.current) clearTimeout(autosaveDebounceRef.current);
    };
  }, [html, sampleData, loading, persistDraft]);

  async function handlePublish() {
    if (!activeVersion) return;
    setSaving(true);
    setError(null);
    try {
      const published = await adminApi.reports.publishTemplate(reportId, activeVersion.id);
      activeVersionRef.current = published;
      setActiveVersion(published);
      setVersions((prev) => prev.map((v) => (v.id === published.id ? published : v)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to publish");
    } finally {
      setSaving(false);
    }
  }

  async function handleDownloadPreview() {
    setDownloading(true);
    setError(null);
    try {
      const { blob, filename } = await adminApi.reports.exportTemplate(reportId, {
        htmlContent: html,
        sampleDataJson: sampleData,
        format: downloadFormat,
        useLiveData,
        params: useLiveData ? liveParams : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename.includes(".") ? filename : `preview.${downloadFormat}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download preview");
    } finally {
      setDownloading(false);
    }
  }

  async function handleDeleteVersion() {
    if (!pendingDeleteVersion) return;
    setDeletingVersion(true);
    try {
      await adminApi.reports.deleteTemplateVersion(reportId, pendingDeleteVersion.id);
      const remaining = versions.filter((v) => v.id !== pendingDeleteVersion.id);
      setVersions(remaining);
      if (activeVersion?.id === pendingDeleteVersion.id) {
        const next = remaining.find((v) => v.status === "DRAFT") ?? remaining[remaining.length - 1] ?? null;
        activeVersionRef.current = next;
        setActiveVersion(next);
        if (next) {
          const loadedHtml = next.htmlContent;
          const loadedSampleData = next.sampleDataJson || DEFAULT_SAMPLE_DATA;
          setHtml(loadedHtml);
          setSampleData(loadedSampleData);
          lastSavedRef.current = { html: loadedHtml, sampleData: loadedSampleData };
        } else {
          setHtml(DEFAULT_HTML);
          setSampleData(DEFAULT_SAMPLE_DATA);
          lastSavedRef.current = { html: DEFAULT_HTML, sampleData: DEFAULT_SAMPLE_DATA };
        }
      }
      setPendingDeleteVersion(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete this version");
      setPendingDeleteVersion(null);
    } finally {
      setDeletingVersion(false);
    }
  }

  function loadVersion(version: ReportTemplateVersion) {
    activeVersionRef.current = version;
    setActiveVersion(version);
    const loadedHtml = version.htmlContent;
    const loadedSampleData = version.sampleDataJson || DEFAULT_SAMPLE_DATA;
    setHtml(loadedHtml);
    setSampleData(loadedSampleData);
    lastSavedRef.current = { html: loadedHtml, sampleData: loadedSampleData };
  }

  if (loading) return <p>Loading...</p>;

  const landscape = report?.defaultPageLayout === "LANDSCAPE";
  const paperW = landscape ? PAPER_LONG : PAPER_SHORT;
  const paperH = landscape ? PAPER_SHORT : PAPER_LONG;
  const fitScale = canvasWidth > 0 ? Math.min(1.25, Math.max(0.3, (canvasWidth - 48) / paperW)) : 1;
  const scale = zoom === "fit" ? fitScale : zoom;

  return (
    <div className="designer-page">
      <ReportTabs reportId={reportId} title={report?.displayName ?? "Report"} />
      {error && <div className="error-text">{error}</div>}

      <div className="designer-toolbar">
        <div className="designer-toolbar-title">
          <span className="studio-label">Report Studio —</span> {report?.displayName}
          <span className="live-pulse" title="Live preview" />
        </div>
        <div className="autosave-status">
          {autosaveStatus === "saving" && "Saving..."}
          {autosaveStatus === "saved" && "All changes saved"}
          {autosaveStatus === "error" && (
            <span className="autosave-status-error">Autosave failed — use Save draft</span>
          )}
        </div>
        <div className="designer-toolbar-actions">
          <button type="button" className="btn-secondary" onClick={() => setShowTemplatePicker(true)}>
            Starter templates
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowSampleData((v) => !v)}>
            {showSampleData ? "Hide sample data" : "Edit sample data"}
          </button>
          <button
            type="button"
            className={useLiveData ? "btn-primary" : "btn-secondary"}
            onClick={() => setUseLiveData((v) => !v)}
            title="Render using real data fetched from your configured endpoint and run through your field mapping, instead of the sample JSON"
          >
            {useLiveData ? "Live data: ON" : "Use live data"}
          </button>
          {versions.length > 0 && (
            <>
              <select
                value={activeVersion?.id ?? ""}
                onChange={(e) => {
                  const v = versions.find((ver) => ver.id === e.target.value);
                  if (v) loadVersion(v);
                }}
              >
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.versionNumber} ({v.status})
                  </option>
                ))}
              </select>
              {activeVersion && (
                <button
                  type="button"
                  className="btn-secondary"
                  title="Delete this version"
                  onClick={() => setPendingDeleteVersion(activeVersion)}
                >
                  Delete version
                </button>
              )}
            </>
          )}
          <span className="designer-toolbar-divider" />
          <select value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value as typeof downloadFormat)}>
            {EXPORT_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </select>
          <button type="button" className="btn-secondary" onClick={handleDownloadPreview} disabled={downloading}>
            {downloading ? "Downloading..." : "Download preview"}
          </button>
          <span className="designer-toolbar-divider" />
          <button type="button" className="btn-secondary" onClick={handleSaveDraft} disabled={saving}>
            {saving ? "Saving..." : "Save draft"}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handlePublish}
            disabled={saving || !activeVersion || activeVersion.status !== "DRAFT"}
          >
            Publish
          </button>
        </div>
      </div>

      {showTemplatePicker && (
        <div className="page-panel">
          <div className="page-header" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Start from a template</h3>
            <button type="button" className="btn-secondary" onClick={() => setShowTemplatePicker(false)}>
              Close
            </button>
          </div>
          <p className="help-text" style={{ marginBottom: 14 }}>
            Pick a starting layout — colors, logo, and company details fill in automatically from
            your <Link to="/settings/branding">Branding</Link> settings. You can edit everything
            afterward.
          </p>
          <div className="starter-template-grid">
            {STARTER_TEMPLATES.map((tpl) => (
              <div key={tpl.id} className="starter-template-card">
                <div className={`starter-template-thumb starter-template-thumb-${tpl.layout}`}>
                  <span />
                  <span />
                  <span />
                </div>
                <div className="starter-template-name">{tpl.name}</div>
                <div className="starter-template-description">{tpl.description}</div>
                <button type="button" className="btn-primary" onClick={() => applyStarterTemplate(tpl)}>
                  Use this layout
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showSampleData && (
        <div className="page-panel">
          <h4>Sample data (used for live preview)</h4>
          {useLiveData ? (
            <p className="help-text">
              Live data is on — the preview renders real data fetched from your configured
              endpoint and run through your field mapping, so this sample JSON is ignored. Turn
              off "Use live data" above to edit and preview against it again.
            </p>
          ) : (
            <JsonEditor value={sampleData} onChange={setSampleData} rows={8} />
          )}
        </div>
      )}

      {html === DEFAULT_HTML && !showTemplatePicker && (
        <div className="designer-tip">
          <strong>New here?</strong> This is a starter template — edit the HTML on the left and
          your changes render live on the right. Click any variable in the panel to insert it at
          your cursor. Add report parameters and connect a data source to unlock more variables.
        </div>
      )}

      <div className="designer-viewbar">
        <div className="segmented" role="group" aria-label="Workspace layout">
          {VIEW_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={viewMode === m.id ? "segmented-active" : undefined}
              onClick={() => setViewMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <span className="designer-viewbar-hint">
          {landscape ? "A4 landscape" : "A4 portrait"} · {activeVersion ? `v${activeVersion.versionNumber} ${activeVersion.status.toLowerCase()}` : "unsaved"}
        </span>
        {viewMode !== "code" && (
          <div className="segmented designer-zoom" role="group" aria-label="Preview zoom">
            {ZOOM_LEVELS.map((z) => (
              <button
                key={String(z.id)}
                type="button"
                className={zoom === z.id ? "segmented-active" : undefined}
                onClick={() => setZoom(z.id)}
              >
                {z.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={`designer-layout designer-layout-${viewMode}`}>
        <div className="designer-palette">
          <div className="designer-pane-header">Variables</div>
          <div className="designer-pane-body">
            <div className="variable-group-label">Always available</div>
            {BUILT_IN_VARIABLES.map((v) => (
              <button
                key={v}
                type="button"
                className="variable-chip"
                onClick={() => insertVariable(v)}
                title="Insert into editor"
              >
                {`{{${v}}}`}
              </button>
            ))}

            {variables.length > 0 ? (
              <>
                <div className="variable-group-label" style={{ marginTop: 16 }}>
                  Your fields
                </div>
                {variables.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="variable-chip"
                    onClick={() => insertVariable(v)}
                    title="Insert into editor"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </>
            ) : (
              <p className="help-text" style={{ marginTop: 16 }}>
                Add <Link to={`/reports/${reportId}/params`}>parameters</Link> or{" "}
                <Link to={`/reports/${reportId}/mapping`}>field mappings</Link> to unlock more
                variables here.
              </p>
            )}

            <div className="variable-group-label" style={{ marginTop: 16 }}>
              Snippets
            </div>
            <p className="help-text" style={{ marginTop: 0, marginBottom: 8 }}>
              Ready-made blocks — insert at your cursor, then edit freely.
            </p>
            {SNIPPETS.map((s) => (
              <button
                key={s.id}
                type="button"
                className="variable-chip"
                style={{ display: "block", width: "100%", textAlign: "left" }}
                onClick={() => insertSnippet(s.html)}
                title={s.description}
              >
                {s.name}
              </button>
            ))}

            <div className="variable-group-label" style={{ marginTop: 16 }}>
              My snippets
            </div>
            {customSnippets.length === 0 && savingSnippetName === null && (
              <p className="help-text" style={{ marginTop: 0, marginBottom: 8 }}>
                Highlight some HTML below and save it here to reuse across reports.
              </p>
            )}
            {customSnippets.map((s) => (
              <div key={s.id} style={{ display: "flex", gap: 4, marginBottom: 7 }}>
                <button
                  type="button"
                  className="variable-chip"
                  style={{ display: "block", flex: 1, textAlign: "left", marginBottom: 0 }}
                  onClick={() => insertSnippet(s.htmlContent)}
                  title="Insert into editor"
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  style={{ padding: "0 8px" }}
                  title="Delete this snippet"
                  onClick={() => setPendingDeleteSnippet(s)}
                >
                  ×
                </button>
              </div>
            ))}

            {savingSnippetName === null ? (
              <button type="button" className="btn-secondary" style={{ width: "100%" }} onClick={startSaveSelectionAsSnippet}>
                Save selection as snippet
              </button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <input
                  autoFocus
                  placeholder="Snippet name"
                  value={savingSnippetName}
                  onChange={(e) => setSavingSnippetName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && confirmSaveSnippet()}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ flex: 1 }}
                    onClick={confirmSaveSnippet}
                    disabled={savingSnippet || !savingSnippetName.trim()}
                  >
                    {savingSnippet ? "Saving..." : "Save"}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setSavingSnippetName(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="designer-editor">
          <div className="designer-pane-header">HTML template</div>
          <div className="designer-editor-monaco">
            <Editor
              defaultLanguage="html"
              value={html}
              theme={isDark ? "vs-dark" : "vs"}
              onChange={(value) => setHtml(value ?? "")}
              onMount={(ed) => {
                editorRef.current = ed;
              }}
              options={{
                minimap: { enabled: false },
                fontSize: monacoFontSize,
                wordWrap: "on",
                automaticLayout: true,
                scrollBeyondLastLine: false,
                padding: { top: 10, bottom: 10 },
                renderLineHighlight: "gutter",
              }}
            />
          </div>
        </div>

        <div className="designer-preview">
          <div className="designer-pane-header">
            Live preview
            {!previewError && <span className="live-pulse" style={{ marginLeft: 6 }} />}
            {useLiveData && (
              <span className="live-data-badge" title="Rendering real fetched + mapped data">
                Real data
              </span>
            )}
          </div>
          {previewError ? (
            <div className="error-text" style={{ padding: 12 }}>
              {previewError}
            </div>
          ) : (
            <div className="designer-preview-canvas" ref={canvasRef}>
              {/* The wrapper takes the scaled size (transform doesn't affect layout) so the
                  canvas scrolls correctly at any zoom; the iframe is a true page-size sheet. */}
              <div className="designer-paper" style={{ width: paperW * scale, height: paperH * scale }}>
                <iframe
                  title="Live preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  style={{ width: paperW, height: paperH, transform: `scale(${scale})` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDeleteVersion}
        title="Delete this version"
        message={
          pendingDeleteVersion
            ? `Delete v${pendingDeleteVersion.versionNumber} (${pendingDeleteVersion.status})? This can't be undone.`
            : ""
        }
        confirmLabel={deletingVersion ? "Deleting..." : "Delete"}
        danger
        onConfirm={handleDeleteVersion}
        onCancel={() => setPendingDeleteVersion(null)}
      />

      <ConfirmDialog
        open={!!pendingDeleteSnippet}
        title="Delete this snippet"
        message={pendingDeleteSnippet ? `Delete "${pendingDeleteSnippet.name}"? This can't be undone.` : ""}
        confirmLabel={deletingSnippet ? "Deleting..." : "Delete"}
        danger
        onConfirm={handleDeleteSnippet}
        onCancel={() => setPendingDeleteSnippet(null)}
      />
    </div>
  );
}
